import { useState, useEffect, forwardRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, X, Smartphone } from "lucide-react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const InstallPrompt = forwardRef<HTMLDivElement>((_props, _ref) => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showBanner, setShowBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Don't show if already installed or dismissed recently
    const dismissed = localStorage.getItem("install-dismissed");
    if (dismissed && Date.now() - Number(dismissed) < 3 * 24 * 60 * 60 * 1000) return;

    // Check if already in standalone mode
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    // Detect iOS
    const ua = navigator.userAgent;
    const isiOS = /iPad|iPhone|iPod/.test(ua) && !(window as any).MSStream;
    setIsIOS(isiOS);

    if (isiOS) {
      // Show banner after a short delay on iOS
      const timer = setTimeout(() => setShowBanner(true), 2500);
      return () => clearTimeout(timer);
    }

    // Android / Desktop — listen for beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setTimeout(() => setShowBanner(true), 2500);
    };

    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const handleInstall = async () => {
    if (isIOS) {
      setShowIOSGuide(true);
      return;
    }
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") {
      setShowBanner(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowBanner(false);
    setShowIOSGuide(false);
    localStorage.setItem("install-dismissed", String(Date.now()));
  };

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-6 sm:max-w-sm"
        >
          <div className="relative rounded-lg border border-primary/30 bg-card/95 backdrop-blur-xl shadow-2xl shadow-primary/10 overflow-hidden">
            {/* Accent strip */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary" />

            <button
              onClick={handleDismiss}
              className="absolute top-3 right-3 p-1 rounded-sm text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>

            {!showIOSGuide ? (
              <div className="p-4 pt-5">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center">
                    <Smartphone className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground text-sm">
                      Get the Ohm & Ply App
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                      Install for instant access to box builder, wiring diagrams & AI — works offline.
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleInstall}
                  className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-semibold text-sm rounded-md shadow-hard hover:brightness-110 transition-all active:scale-[0.98]"
                >
                  <Download className="w-4 h-4" />
                  Install App — It's Free
                </button>
              </div>
            ) : (
              <div className="p-4 pt-5">
                <p className="font-semibold text-foreground text-sm mb-2">
                  How to install on iPhone
                </p>
                <ol className="text-xs text-muted-foreground space-y-1.5 list-decimal list-inside leading-relaxed">
                  <li>
                    Tap the <span className="text-foreground font-medium">Share</span> button{" "}
                    <span className="inline-block align-middle">⬆️</span> in Safari
                  </li>
                  <li>
                    Scroll down and tap{" "}
                    <span className="text-foreground font-medium">"Add to Home Screen"</span>
                  </li>
                  <li>
                    Tap <span className="text-foreground font-medium">"Add"</span> — done!
                  </li>
                </ol>
                <button
                  onClick={handleDismiss}
                  className="mt-3 w-full px-4 py-2 bg-secondary text-foreground font-medium text-sm rounded-md hover:bg-secondary/80 transition-colors"
                >
                  Got it
                </button>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

InstallPrompt.displayName = "InstallPrompt";

export default InstallPrompt;
